import { AlertCircle, CheckCircle2, LoaderCircle, TriangleAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { IconButton } from './primitives.js';

export function InlineAlert({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title: string;
  children: ReactNode;
}) {
  const Icon = tone === 'success' ? CheckCircle2 : tone === 'warning' ? TriangleAlert : AlertCircle;
  return (
    <div
      className={`dui-alert dui-alert--${tone}`}
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
    >
      <Icon size={17} aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export function SaveStatus({ status }: { status: 'saved' | 'saving' | 'unsaved' | 'error' }) {
  const labels = {
    saved: 'All changes saved',
    saving: 'Saving changes',
    unsaved: 'Unsaved changes',
    error: 'Could not save',
  };
  return (
    <span className={`dui-save-status dui-save-status--${status}`}>
      {status === 'saving' ? (
        <LoaderCircle size={13} aria-hidden="true" />
      ) : (
        <span aria-hidden="true" />
      )}
      {labels[status]}
    </span>
  );
}

export function Skeleton({ width = '100%' }: { width?: string }) {
  return <span className="dui-skeleton" style={{ width }} aria-hidden="true" />;
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="dui-empty-state">
      <span className="dui-empty-state__mark" aria-hidden="true" />
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function Toast({ children, onDismiss }: { children: ReactNode; onDismiss?(): void }) {
  return (
    <div className="dui-toast">
      <span role="status">{children}</span>
      {onDismiss ? (
        <IconButton icon={X} label="Dismiss notification" variant="quiet" onClick={onDismiss} />
      ) : null}
    </div>
  );
}
